import { Box, Typography, Skeleton } from '@mui/material';
import useCustomTheme from '../../../theme/useCustomTheme';
import '@fontsource/rubik';

function TeamRow({ team, maxCount, isLoading }) {
  const [theme] = useCustomTheme();
  const pct = maxCount > 0 ? (team.count / maxCount) * 100 : 0;
  const secondaryColor = theme.palette.secondary.main;

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '130px 1fr 36px',
          alignItems: 'center',
          gap: 2,
          py: 1.25,
          borderTop: '1px solid #E9EBE9',
        }}
      >
        <Skeleton width="80%" height={16} />
        <Skeleton width="100%" height={14} sx={{ borderRadius: '4px' }} />
        <Skeleton width={24} height={16} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '110px 1fr 36px', lg: '130px 1fr 36px' },
        alignItems: 'center',
        gap: 2,
        py: 1.25,
        borderTop: '1px solid #E9EBE9',
        '&:first-of-type': { borderTop: 'none' },
      }}
    >
      <Typography
        sx={{
          fontFamily: 'Rubik',
          fontSize: { xs: '12px', lg: '13px', xl: '14px' },
          fontWeight: 500,
          color: '#08231B',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {team.name}
      </Typography>

      <Box
        sx={{
          height: 14,
          backgroundColor: '#ececea',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            width: `${pct}%`,
            height: '100%',
            backgroundColor: secondaryColor,
            borderRadius: '4px',
            transition: 'width 0.4s ease',
          }}
        />
      </Box>

      <Typography
        sx={{
          fontFamily: 'Rubik',
          fontSize: { xs: '13px', lg: '14px', xl: '15px' },
          fontWeight: 600,
          textAlign: 'right',
          color: '#08231B',
        }}
      >
        {team.count}
      </Typography>
    </Box>
  );
}

export default function TeamBreakdownCard({ teamBreakdown, isLoading }) {
  const teams = teamBreakdown?.teams || [];
  const total = teamBreakdown?.total ?? 0;
  const periodLabel = teamBreakdown?.period_label || 'last 6 months';
  const maxCount = teams.length > 0 ? Math.max(...teams.map((t) => t.count)) : 1;

  const skeletonRows = [1, 2, 3, 4];

  return (
    <Box
      id="cd-team-chart"
      sx={{
        backgroundColor: '#ffffff',
        border: '1px solid #E9EBE9',
        borderRadius: '14px',
        boxShadow: '0 1px 2px rgba(8,35,27,0.04)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: '20px',
          pt: '18px',
          pb: '4px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: 'Rubik',
              fontSize: { xs: '14px', lg: '15px', xl: '16px' },
              fontWeight: 700,
              color: '#08231B',
            }}
          >
            Videos Created by Team
          </Typography>
          <Typography
            sx={{
              fontFamily: 'Rubik',
              fontSize: { xs: '11px', lg: '12px', xl: '13px' },
              color: '#6b7470',
              mt: 0.25,
            }}
          >
            {total} videos · {periodLabel}
          </Typography>
        </Box>
      </Box>

      {/* Rows */}
      <Box sx={{ px: '20px', pb: '20px', pt: '8px' }}>
        {isLoading
          ? skeletonRows.map((k) => (
              <Box
                key={k}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '130px 1fr 36px',
                  alignItems: 'center',
                  gap: 2,
                  py: 1.25,
                  borderTop: k === 1 ? 'none' : '1px solid #E9EBE9',
                }}
              >
                <Skeleton width="80%" height={16} />
                <Skeleton width="100%" height={14} sx={{ borderRadius: '4px' }} />
                <Skeleton width={24} height={16} />
              </Box>
            ))
          : teams.map((team) => (
              <TeamRow key={team.name} team={team} maxCount={maxCount} isLoading={false} />
            ))}
      </Box>
    </Box>
  );
}
